import React, { useRef } from 'react';
import { Badge } from '../../badge/badge';
import { BadgeType } from '../../badge/badge.styles';
import * as S from './underline-tabs.styles';

export interface IUnderlineTab<T extends string> {
    id: T;
    label: React.ReactNode;
    /**
     * Shown as a badge after the label. Omit it for no badge. Pass a placeholder string such as
     * `'—'` while a count is unknown — never `0`, which is a claim that there are none (NS-3).
     */
    count?: number | string;
}

export interface IUnderlineTabsProps<T extends string> {
    tabs: IUnderlineTab<T>[];
    activeTab: T;
    onTabChange: (tab: T) => void;
    className?: string;
}

/**
 * A row of text tabs with an underline on the active one and an optional count badge.
 *
 * Extracted from the patient statement's tab bar so the Roster screen (FE-109) could use the
 * same one rather than a copy. For the folder-style tabs with a content panel, see `TabContainer`.
 *
 * Tab semantics per the WAI-ARIA tabs pattern: each tab is `role="tab"` with `aria-selected`, only
 * the active tab is in the Tab order, and Left/Right (wrapping), Home and End move to another tab
 * and select it.
 */
export function UnderlineTabs<T extends string>({
    tabs,
    activeTab,
    onTabChange,
    className,
}: IUnderlineTabsProps<T>): React.ReactElement {
    const listRef = useRef<HTMLDivElement>(null);

    const select = (index: number): void => {
        const wrapped = (index + tabs.length) % tabs.length;
        onTabChange(tabs[wrapped].id);
        listRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[wrapped]?.focus();
    };

    const handleKeyDown = (index: number) => (e: React.KeyboardEvent): void => {
        const target: Record<string, number> = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: tabs.length - 1 };
        if (e.key in target) {
            e.preventDefault();
            select(target[e.key]);
        }
    };

    return (
        <S.Tabs ref={listRef} role="tablist" className={className}>
            {tabs.map((tab, index) => {
                const active = activeTab === tab.id;
                return (
                    <S.Tab
                        key={tab.id}
                        type="button"
                        variant="unstyled"
                        role="tab"
                        aria-selected={active}
                        tabIndex={active ? 0 : -1}
                        $active={active}
                        onClick={(): void => onTabChange(tab.id)}
                        onKeyDown={handleKeyDown(index)}
                    >
                        {tab.label}
                        {tab.count != null && (
                            <S.CountWrap>
                                <Badge type={active ? BadgeType.BLUE_LIGHT : undefined} size="small">
                                    {tab.count}
                                </Badge>
                            </S.CountWrap>
                        )}
                    </S.Tab>
                );
            })}
        </S.Tabs>
    );
}
